/**
 * Finnish Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const fi: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Kojelauta',
  'nav.funds': 'Rahastot',
  'nav.investments': 'Sijoitukset',
  'nav.investees': 'Sijoituskohteet',
  'nav.esg': 'ESG Katsaus',
  'nav.compliance': 'Vaatimustenmukaisuus',
  'nav.reports': 'Raportit',
  'nav.settings': 'Asetukset',
  'nav.users': 'Käyttäjät',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indikaattorit',
  'nav.taxonomy': 'EU-taksonomia',
  'nav.filings': 'Ilmoitukset',
  'nav.calendar': 'Kalenteri',
  'nav.submit': 'Lähetä tiedot',
  'nav.submissions': 'Omat lähetykset',
  'nav.benchmarks': 'Vertailuarvot',
  'nav.messages': 'Viestit',
  'nav.portfolio': 'Salkku',
  'nav.performance': 'Rahastojen tulokset',
  'nav.esgSummary': 'ESG Yhteenveto',
  'nav.pipeline': 'Putki',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Vaikuttavuusmittarit',
  'nav.sdg': 'SDG-kartoitus',
  'nav.audit': 'Tarkastusjälki',
  'nav.snapshots': 'Tietovedokset',
  'nav.knowledgeGraph': 'Tietograafi',
  'nav.geographic': 'Maantieteellinen',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Avustaja',
  'nav.tasks': 'Tehtävät',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Sääntelymonitori',

  // Common Actions
  'action.create': 'Luo',
  'action.edit': 'Muokkaa',
  'action.delete': 'Poista',
  'action.save': 'Tallenna',
  'action.cancel': 'Peruuta',
  'action.submit': 'Lähetä',
  'action.approve': 'Hyväksy',
  'action.reject': 'Hylkää',
  'action.download': 'Lataa',
  'action.export': 'Vie',
  'action.import': 'Tuo',
  'action.filter': 'Suodata',
  'action.search': 'Hae',
  'action.refresh': 'Päivitä',
  'action.close': 'Sulje',
  'action.back': 'Takaisin',
  'action.next': 'Seuraava',
  'action.previous': 'Edellinen',
  'action.viewAll': 'Näytä kaikki',
  'action.viewDetails': 'Näytä tiedot',
  'action.generate': 'Luo raportti',
  'action.signOut': 'Kirjaudu ulos',
  'action.addNew': 'Lisää uusi',
  'action.invite': 'Kutsu',

  // Statuses
  'status.draft': 'Luonnos',
  'status.submitted': 'Lähetetty',
  'status.validated': 'Vahvistettu',
  'status.approved': 'Hyväksytty',
  'status.rejected': 'Hylätty',
  'status.pending': 'Odottaa',
  'status.inProgress': 'Käynnissä',
  'status.overdue': 'Myöhässä',
  'status.compliant': 'Vaatimustenmukainen',
  'status.filed': 'Ilmoitettu',
  'status.active': 'Aktiivinen',
  'status.inactive': 'Ei-aktiivinen',
  'status.closed': 'Suljettu',
  'status.fundraising': 'Varainhankinta',
  'status.investing': 'Sijoittaminen',
  'status.harvesting': 'Tuotonkorjuu',
  'status.validating': 'Validointi',
  'status.review': 'Tarkistuksessa',
  'status.monitoring': 'Seuranta',
  'status.exiting': 'Irtautuminen',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Due Diligence',
  'status.disbursed': 'Maksettu',
  'status.restructured': 'Uudelleenjärjestelty',
  'status.matured': 'Erääntynyt',
  'status.written_off': 'Poistettu',
  'status.exited': 'Irtautunut',
  'status.incomplete': 'Puutteellinen',
  'status.open': 'Avoin',
  'status.in_progress': 'Käynnissä',
  'status.completed': 'Valmis',
  'status.cancelled': 'Peruutettu',
  'status.accepted': 'Hyväksytty',
  'status.failed': 'Epäonnistunut',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Salkkuyhteenveto',
  'dashboard.complianceStatus': 'Vaatimustenmukaisuuden tila',
  'dashboard.collectionProgress': 'Keruun edistyminen',
  'dashboard.activityFeed': 'Toiminta',
  'dashboard.quickActions': 'Pikatoiminnot',
  'dashboard.investmentPipeline': 'Sijoitusputki',
  'dashboard.totalFunds': 'Rahastoja yhteensä',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktiiviset sijoitukset',
  'dashboard.totalInvestees': 'Sijoituskohteet',
  'dashboard.recentActivity': 'Viimeaikainen toiminta',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Sitoutunut yhteensä',
  'lp.capitalCalled': 'Kutsuttu pääoma',
  'lp.distributed': 'Jaettu',
  'lp.netPosition': 'Nettopositio',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Rahastojen tulokset',
  'lp.esgSummary': 'ESG Yhteenveto',
  'lp.reportLibrary': 'Raporttikirjasto',
  'lp.taxonomyAligned': 'Taksonomian mukainen',
  'lp.paiCoverage': 'PAI Kattavuus',
  'lp.portfolioPerformance': 'LP Salkun tulokset',

  // Fund Labels
  'fund.name': 'Rahaston nimi',
  'fund.code': 'Rahaston koodi',
  'fund.vintage': 'Perustamisvuosi',
  'fund.size': 'Rahaston koko',
  'fund.targetSize': 'Tavoitekoko',
  'fund.currency': 'Valuutta',
  'fund.strategy': 'Strategia',
  'fund.status': 'Tila',
  'fund.sfdrCategory': 'SFDR Luokka',
  'fund.geographyFocus': 'Maantieteellinen painopiste',
  'fund.sectorFocus': 'Sektoripainopiste',

  // Form Labels
  'form.name': 'Nimi',
  'form.email': 'Sähköposti',
  'form.country': 'Maa',
  'form.amount': 'Määrä',
  'form.currency': 'Valuutta',
  'form.date': 'Päivämäärä',
  'form.description': 'Kuvaus',
  'form.notes': 'Muistiinpanot',
  'form.type': 'Tyyppi',
  'form.period': 'Kausi',
  'form.year': 'Vuosi',
  'form.required': 'Pakollinen',
  'form.optional': 'Valinnainen',

  // Table Headers
  'table.fund': 'Rahasto',
  'table.commitment': 'Sitoumus',
  'table.called': 'Kutsuttu',
  'table.distributed': 'Jaettu',
  'table.tvpi': 'TVPI',
  'table.status': 'Tila',
  'table.regulation': 'Sääntely',
  'table.filingType': 'Ilmoitustyyppi',
  'table.reportingPeriod': 'Kausi',
  'table.createdAt': 'Luotu',
  'table.actions': 'Toiminnot',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU-taksonomia',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Tietojen kattavuus',
  'compliance.nextDeadline': 'Seuraava määräaika',

  // Reports
  'report.quarterly': 'Neljännesvuosiraportti',
  'report.annual': 'Vuosiraportti',
  'report.paiStatement': 'PAI Lausunto',
  'report.taxonomyReport': 'Taksonomiaraportti',
  'report.generatedAt': 'Luotu',
  'report.version': 'Versio',

  // General
  'general.loading': 'Ladataan...',
  'general.noData': 'Ei tietoja saatavilla',
  'general.error': 'Tapahtui virhe',
  'general.retry': 'Yritä uudelleen',
  'general.total': 'Yhteensä',
  'general.page': 'Sivu',
  'general.of': '/',
  'general.showing': 'Näytetään',
  'general.results': 'tulosta',
  'general.language': 'Kieli',
  'general.english': 'Englanti',
  'general.french': 'Ranska',

  // Tour
  'tour.takeTour': 'Aloita kierros',
  'tour.skip': 'Ohita',
  'tour.next': 'Seuraava',
  'tour.prev': 'Edellinen',
  'tour.finish': 'Valmis',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigointi',
  'tour.sidebarContent': 'Käytä sivupalkkia navigoidaksesi portfolion hallinnan, ESG-tietojen keruun, vaatimustenmukaisuuden, raporttien ja analytiikkatyökalujen välillä.',
  'tour.kpisTitle': 'Portfolion yleiskatsaus',
  'tour.kpisContent': 'Nämä kortit näyttävät portfoliosi yhdellä silmäyksellä — rahastot yhteensä, AUM, aktiiviset sijoitukset ja sijoituskohteiden määrä.',
  'tour.actionsTitle': 'Pikatoiminnot',
  'tour.actionsContent': 'Luo rahastoja, lisää sijoituksia, kutsu sijoituskohteita ja luo raportteja yhdellä napsautuksella.',
  'tour.languageTitle': 'Kieli',
  'tour.languageContent': 'Vaihda 24 virallisen EU-kielen välillä. Koko käyttöliittymä käännetään välittömästi.',
  'tour.aiTitle': 'Tekoälyavustaja',
  'tour.aiContent': 'Kysy portfoliostasi milloin tahansa. Tekoälyavustajalla on täysi konteksti rahastoistasi ja sijoituksistasi.',
  'action.createFund': 'Luo rahasto',
  'action.editFund': 'Muokkaa rahastoa',
  'action.addInvestment': 'Lisää sijoitus',
  'action.addInvestee': 'Lisää sijoituskohde',
  'action.editInvestee': 'Muokkaa kohdetta',
  'action.createTemplate': 'Luo malli',
  'action.createSubmission': 'Luo lähetys',
  'action.createFiling': 'Luo ilmoitus',
  'action.generateReport': 'Luo raportti',
  'action.inviteUser': 'Kutsu käyttäjä',
  'action.sendInvitation': 'Lähetä kutsu',
  'action.saveChanges': 'Tallenna muutokset',
  'form.fundName': 'Rahaston nimi',
  'form.fundCode': 'Rahastokoodi',
  'form.sfdrCategory': 'SFDR Kategoria',
  'form.baseCurrency': 'Perusvaluutta',
  'form.targetSize': 'Tavoitekoko',
  'form.vintageYear': 'Perustamisvuosi',
  'form.geographyFocus': 'Maantieteellinen fokus',
  'form.sectorFocus': 'Sektorifokus',
  'form.investmentCode': 'Sijoituskoodi',
  'form.instrumentType': 'Instrumenttityyppi',
  'form.maturityDate': 'Eräpäivä',
  'form.sector': 'Sektori',
  'form.countryCode': 'Maakoodi',
  'form.entityType': 'Yhteisötyyppi',
  'form.region': 'Alue',
  'form.staffCount': 'Henkilöstömäärä',
  'form.borrowers': 'Lainanottajat',
  'form.contactName': 'Yhteyshenkilön nimi',
  'form.contactEmail': 'Yhteyssähköposti',
  'form.templateName': 'Mallin nimi',
  'form.templateType': 'Mallityyppi',
  'form.indicators': 'Indikaattorit',
  'form.reportingPeriod': 'Raportointijakso',
  'form.reportingYear': 'Raportointivuosi',
  'form.regulation': 'Sääntely',
  'form.filingType': 'Ilmoitustyyppi',
  'form.reportType': 'Raporttityyppi',
  'form.emailAddress': 'Sähköpostiosoite',
  'form.role': 'Rooli',
  'form.selectFund': 'Valitse rahasto...',
  'form.selectInvestee': 'Valitse kohde...',
  'table.code': 'Koodi',
  'table.investee': 'Kohde',
  'table.instrument': 'Instrumentti',
  'table.amount': 'Summa',
  'table.currency': 'Valuutta',
  'table.maturity': 'Eräpäivä',
  'table.name': 'Nimi',
  'table.country': 'Maa',
  'table.type': 'Tyyppi',
  'table.period': 'Jakso',
  'table.completion': 'Valmistuminen',
  'table.submitted': 'Lähetetty',
  'table.version': 'Versio',
  'table.indicators': 'Indikaattorit',
  'table.dueDate': 'Eräpäivä',
  'table.generated': 'Luotu',
  'table.email': 'Sähköposti',
  'table.role': 'Rooli',
  'error.createFailed': 'Luonti epäonnistui. Yritä uudelleen.',
  'error.updateFailed': 'Päivitys epäonnistui. Yritä uudelleen.',
  'error.loadFailed': 'Tietojen lataus epäonnistui.',

  // Sidebar Sections
  'sections.portfolio': 'SALKKU',
  'sections.dataCollection': 'TIEDONKERUU',
  'sections.intelligence': 'ANALYTIIKKA',
  'sections.ecosystem': 'EKOSYSTEEMI',
  'sections.admin': 'HALLINTO',
  'sections.other': 'MUU',

  // Page Titles
  'pages.messagesTitle': 'Viestit',
  'pages.taxonomyTitle': 'EU-taksonomia',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Tietograafi',
  'pages.benchmarksTitle': 'Vertailutiedot',
  'pages.mcpTitle': 'MCP-integraatio',
  'pages.tasksTitle': 'Tehtävät',
  'pages.submitDataTitle': 'Lähetä ESG-tiedot',

  // Empty States
  'empty.benchmarksComingSoon': 'Vertailutiedot tulossa pian',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'EU-taksonomia',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Muu',
  'labels.critical': 'Kriittinen',
  'labels.high': 'Korkea',
  'labels.medium': 'Keskitaso',
  'labels.low': 'Matala',
};
